import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output
}                                from '@angular/core';
import { BaseComponent }         from '@core/bases/base-component/base.component';
import {
	FilterTextType,
	IActiveFilterData,
	IFilter
}                                from '@shared/list-operations/model/filter.model';
import {
	ListOperationsIdentifierEnum,
	ListOperationsResetEnum,
	ListOperationsResetModeEnum,
	ListOperationsResetType
}                                from '@shared/list-operations/model/list-operations.model';
import { ListOperationsService } from '@shared/list-operations/services/list-operations.service';
import {
	Subject,
	Subscription
}                                from 'rxjs';
import {
	debounceTime,
	takeUntil
}                                from 'rxjs/operators';


@Component( {
	selector: 'app-text-filter[listOperationIdentifier]',
	templateUrl: './text-filter.component.html',
	styleUrls: [ './text-filter.component.scss' ]
} )
export class TextFilterComponent extends BaseComponent implements OnInit, OnDestroy {

	private _blockEmit: boolean;

	private _listOperationIdentifier: ListOperationsIdentifierEnum;

	private _defaultFilterData: IFilter;

	private _debounceTimer = 400;

	private _id: number;

	private _resetListOperationsSubscription: Subscription;

	private _revertValue: string;

	private _textFilterSubject: Subject<FilterTextType> = new Subject();

	private _trimmedTextFilterValue: FilterTextType;

	activeOperation: boolean;

	isFocused: boolean;

	showClearButton: boolean;

	showRevertButton: boolean;

	textFilterValue: FilterTextType;

	get defaultFilterData() {
		return this._defaultFilterData;
	}

	@Input() set defaultFilterData( defaultFilterData: IFilter ) {
		this.initialize( defaultFilterData );
	}

	get listOperationIdentifier(): ListOperationsIdentifierEnum {
		return this._listOperationIdentifier;
	}

	@Input()
	set listOperationIdentifier( listOperationIdentifier: ListOperationsIdentifierEnum ) {
		if ( !listOperationIdentifier ) { return; }

		this._listOperationIdentifier = listOperationIdentifier;

		this._resetListOperationsSubscription?.unsubscribe();

		this._resetListOperationsSubscription =
			this.listOperationsService.select.resetListOperationsData(
				listOperationIdentifier,
				'TextFilterComponent:set listOperationIdentifier'
			)
			.pipe(
				takeUntil( this.ngUnsubscribe )
			)
			.subscribe( ( resetData: ListOperationsResetType ) => {
				if (
					resetData?.listOperator === ListOperationsResetEnum.ALL ||
					resetData?.listOperator === ListOperationsResetEnum.FILTERS_ALL ||
					(
						resetData?.listOperator === ListOperationsResetEnum.FILTER &&
						resetData?.identifier === this._id
					)
				) {

					this._blockEmit = resetData.blockEmit;

					switch ( resetData.resetMode ) {
						case ListOperationsResetModeEnum.CLEAR:
							this.clearControlValue();
							break;

						case ListOperationsResetModeEnum.DEFAULT:
						case ListOperationsResetModeEnum.RESTORE:
							this.resetFilter( resetData.resetMode );
							break;
					}
				}
			} );
	}

	// TODO temporary flag prop to distinguish between old and new, will be removed when old has been completely replaced by new
	@Input() sidebar: boolean;

	@Output() filterItemChange: EventEmitter<IActiveFilterData> = new EventEmitter();

	constructor(
		private readonly listOperationsService: ListOperationsService
	) {
		super();
	}

	ngOnInit(): void {
		this._textFilterSubject
		.pipe(
			debounceTime( this._debounceTimer )
		)
		.subscribe( () => {
			if ( this._blockEmit ) {
				this._blockEmit = false;
				return;
			}

			if ( this.textFilterValue === null ) { return; }
			this.filterItemChange.emit( this.prepareTextFilterDataForEmit );
		} );
	}

	ngOnDestroy(): void {
		this.textFilterValue = null;
		this._textFilterSubject.next( '' );
		this._textFilterSubject.complete();
	}

	private get prepareTextFilterDataForEmit(): IActiveFilterData {
		return {
			param: this._trimmedTextFilterValue
		};
	}

	private initialize( defaultFilterData: IFilter ): void {
		this._id = defaultFilterData.id;
		this._defaultFilterData = defaultFilterData;
		this.setTextFilterValue( defaultFilterData );
		this.isFocused = false;
	}

	private setTextFilterValue( filterData: IFilter ): void {

		this.textFilterValue = filterData?.values?.length
													 ? filterData.values[ 0 ] as FilterTextType
													 : '';

		this._trimmedTextFilterValue = this.textFilterValue?.trim();
		this.emitFilterItemChange();
	}

	private resetFilter( resetMode: ListOperationsResetModeEnum ): void {

		const resetData: IFilter = this.listOperationsService.helper.filterResetDataByResetMode(
			this.listOperationIdentifier,
			this._id,
			resetMode
		);

		this.setTextFilterValue( resetData );
	}

	private updateActionButtonsVisibility(): void {
		const timeout = setTimeout( () => {
			clearTimeout( timeout );
			if ( this._trimmedTextFilterValue ) {
				this._revertValue = this._trimmedTextFilterValue;
				this.showClearButton = true;
				this.activeOperation = true;
			} else {
				this.showClearButton = false;
				this.activeOperation = false;
			}
			this.showRevertButton = this._revertValue
															? !this.showClearButton
															: false;
		}, 0 );
	}

	private emitFilterItemChange(): void {
		this.updateActionButtonsVisibility();
		this._textFilterSubject.next( '' );
	}

	clearControlValue(): void {
		this.onInputChange( '' );
	}

	revertControlValue(): void {
		this.onInputChange( this._revertValue );
	}

	onInputChange( textFilterValue: FilterTextType ): void {
		this.textFilterValue = textFilterValue ?? '';
		this._trimmedTextFilterValue = this.textFilterValue?.trim();
		this.emitFilterItemChange();
	}
}
